import { getPropertyTypeFromCode } from '../../../shared/mappers/property-type.mapper';
import { Report } from '../../models/report.model';
import { ReportHttp } from '../../models/report-http.dto';

export const fromReportHttp = (reportHttp: ReportHttp): Report => ({
  id: reportHttp.id,
  historyLegacyId: reportHttp.history_legacy_id,
  evaluationLegacyId: reportHttp.evaluation_legacy_id,
  type: reportHttp.type,
  userExternalId: reportHttp.user_external_id,
  organizationExternalId: reportHttp.organization_external_id,
  createdAt: reportHttp.created_at,
  propertyType: getPropertyTypeFromCode(reportHttp.property_type),
  addressCity: reportHttp.address_city,
  addressPostalCode: reportHttp.address_postal_code,
  addressStreet: reportHttp.address_street,
  addressCityCode: reportHttp.address_city_code,
  addressInput: reportHttp.address_input,
  addressAccuracyLevel: reportHttp.address_accuracy_level,
  districtName: reportHttp.district_name,
  sections: {
    appraisal: {
      isEnabled: reportHttp.sections.appraisal.is_enabled,
      basicInfo: {
        lead: reportHttp.sections.appraisal.lead,
        comment: reportHttp.sections.appraisal.comment,
        chosenValue: reportHttp.sections.appraisal.chosen_value,
        conclusion: reportHttp.sections.appraisal.conclusion,
        chosenLowValue: reportHttp.sections.appraisal.chosen_low_value,
        chosenHighValue: reportHttp.sections.appraisal.chosen_high_value,
      },
      appraisedValue: reportHttp.sections.appraisal.appraised_value,
      lowEstimate: reportHttp.sections.appraisal.low_estimate,
      highEstimate: reportHttp.sections.appraisal.high_estimate,
      priceM2: reportHttp.sections.appraisal.chosen_price_m2 || 0,
      addedReducedValues:
        reportHttp.sections.appraisal.added_reduced_values.map(
          (addedReducedValue) => ({
            category: addedReducedValue.category,
            value: addedReducedValue.value,
            percentage: addedReducedValue.percentage,
            description: addedReducedValue.description,
          }),
        ),
      appraisedValueWithAddedReducedValues:
        reportHttp.sections.appraisal.appraised_value_with_added_reduced_values,
      lowEstimateWithAddedReducedValues:
        reportHttp.sections.appraisal.low_estimate_with_added_reduced_values,
      lowEstimatePercentageWithAddedReducedValues:
        reportHttp.sections.appraisal
          .low_estimate_percentage_with_added_reduced_values,
      highEstimateWithAddedReducedValues:
        reportHttp.sections.appraisal.high_estimate_with_added_reduced_values,
      highwayAccessEstimatePercentageWithAddedReducedValues:
        reportHttp.sections.appraisal
          .high_estimate_percentage_with_added_reduced_values,
      priceM2WithAddedReducedValues:
        reportHttp.sections.appraisal.price_m2_with_added_reduced_values,
      area: reportHttp.sections.appraisal.area,
      highEstimatePercentage:
        reportHttp.sections.appraisal.high_estimate_percentage,
      lowEstimatePercentage:
        reportHttp.sections.appraisal.low_estimate_percentage,
    },
    references: {
      isEnabled: reportHttp.sections.references.is_enabled,
      isDisplayModeDetailled:
        reportHttp.sections.references.is_display_mode_detailled,
      isConfidential: reportHttp.sections.references.is_confidential,
      isSelection: reportHttp.sections.references.is_selection,
      purchasedCount: reportHttp.sections.references.purchased_count,
      selectedCount: reportHttp.sections.references.selected_count,
    },
    customer: {
      isEnabled: reportHttp.sections.customer.is_enabled,
      firstName: reportHttp.sections.customer.firstname,
      lastName: reportHttp.sections.customer.lastname,
      address: reportHttp.sections.customer.address,
      postalCode: reportHttp.sections.customer.postal_code,
      city: reportHttp.sections.customer.city,
      phone: reportHttp.sections.customer.phone,
      email: reportHttp.sections.customer.email,
    },
    perks: {
      isEnabled: reportHttp.sections.perks.is_enabled,
      items: reportHttp.sections.perks.items,
    },
    drawbacks: {
      isEnabled: reportHttp.sections.drawbacks.is_enabled,
      items: reportHttp.sections.drawbacks.items,
    },
    characteristics: {
      isEnabled: reportHttp.sections.characteristics.is_enabled,
      constructionYear: reportHttp.sections.characteristics.construction_year,
      appartement: {
        energyPerformance:
          reportHttp.sections.characteristics.energy_performance,
        exposure: reportHttp.sections.characteristics.exposure,
        floor: reportHttp.sections.characteristics.floor,
        ghgEmissions: reportHttp.sections.characteristics.ghg_emissions,
        heating: reportHttp.sections.characteristics.heating,
        livingAreaM2: reportHttp.sections.characteristics.living_area_m2,
        livingRoomAreaM2:
          reportHttp.sections.characteristics.living_room_area_m2,
        numberOfBathrooms:
          reportHttp.sections.characteristics.number_of_bathrooms,
        numberOfBedrooms:
          reportHttp.sections.characteristics.number_of_bedrooms,
        numberOfRooms: reportHttp.sections.characteristics.number_of_rooms,
      },
      bienAgricole: {
        cropType: reportHttp.sections.characteristics.crop_type,
        farmBuildingsAreaM2:
          reportHttp.sections.characteristics.farm_buildings_area_m2,
        hasFarmBuildings:
          reportHttp.sections.characteristics.has_farm_buildings,
        hasResidentialBuilding:
          reportHttp.sections.characteristics.has_residential_building,
        landAreaHa: reportHttp.sections.characteristics.land_area_ha,
        residentialBuildingsAreaM2:
          reportHttp.sections.characteristics.residential_buildings_area_m2,
      },
      bienViticole: {
        appellation: reportHttp.sections.characteristics.appellation,
        farmBuildingsAreaM2:
          reportHttp.sections.characteristics.farm_buildings_area_m2,
        grapeVarieties: reportHttp.sections.characteristics.grape_varieties,
        hasFarmBuildings:
          reportHttp.sections.characteristics.has_farm_buildings,
        hasResidentialBuilding:
          reportHttp.sections.characteristics.has_residential_building,
        residentialBuildingsAreaM2:
          reportHttp.sections.characteristics.residential_buildings_area_m2,
        vineyardAreaHa: reportHttp.sections.characteristics.vineyard_area_ha,
      },
      garage: {
        floor: reportHttp.sections.characteristics.floor,
        garageType: reportHttp.sections.characteristics.garage_type,
        numberOfParkingSlots:
          reportHttp.sections.characteristics.number_of_parking_slots,
      },
      immeuble: {
        hasCondominium: reportHttp.sections.characteristics.has_condominium,
        hasElevator: reportHttp.sections.characteristics.has_elevator,
        numberOfUnits: reportHttp.sections.characteristics.number_of_units,
        premisesUsage: reportHttp.sections.characteristics.premisesUsage,
        profitability: reportHttp.sections.characteristics.profitability,
        totalAreaM2: reportHttp.sections.characteristics.total_area_m2,
      },
      localActivite: {
        areaM2: reportHttp.sections.characteristics.area_m2,
        nature: reportHttp.sections.characteristics.nature,
        numberOfParkingSpaces:
          reportHttp.sections.characteristics.number_of_parking_spaces,
        publicTransportNearby:
          reportHttp.sections.characteristics.public_transport_nearby.selectedOptions.map(
            (selectedOption) => selectedOption,
          ),
      },
      maison: {
        energyPerformance:
          reportHttp.sections.characteristics.energy_performance,
        exposure: reportHttp.sections.characteristics.exposure,
        ghgEmissions: reportHttp.sections.characteristics.ghg_emissions,
        landAreaM2: reportHttp.sections.characteristics.land_area_m2,
        level: reportHttp.sections.characteristics.level,
        livingAreaM2: reportHttp.sections.characteristics.living_area_m2,
        livingRoomAreaM2:
          reportHttp.sections.characteristics.living_room_area_m2,
        numberOfBathrooms:
          reportHttp.sections.characteristics.number_of_bathrooms,
        numberOfBedrooms:
          reportHttp.sections.characteristics.number_of_bedrooms,
        numberOfRooms: reportHttp.sections.characteristics.number_of_rooms,
        heating: reportHttp.sections.characteristics.heating,
      },
      terrain: {
        accessibility: reportHttp.sections.characteristics.accessibility,
        areaM2: reportHttp.sections.characteristics.area_m2,
        facadeWidthM2: reportHttp.sections.characteristics.facade_width_m2,
        plotRatio: reportHttp.sections.characteristics.plot_ratio,
        viability: reportHttp.sections.characteristics.viability,
      },
    },
    expert: {
      isEnabled: reportHttp.sections.expert.is_enabled,
      firstName: reportHttp.sections.expert.firstname,
      lastName: reportHttp.sections.expert.lastname,
      email: reportHttp.sections.expert.email,
      experiences: reportHttp.sections.expert.experiences,
      phone: reportHttp.sections.expert.phone,
      role: reportHttp.sections.expert.role,
      roleLabel: reportHttp.sections.expert.role_label,
    },
    organisme: {
      isEnabled: reportHttp.sections.organization.is_enabled,
      name: reportHttp.sections.organization.name,
      addressLine1: reportHttp.sections.organization.address_line1,
      addressLine2: reportHttp.sections.organization.address_line2,
      addressLine3: reportHttp.sections.organization.address_line3,
      postalCode: reportHttp.sections.organization.postal_code,
      city: reportHttp.sections.organization.city,
      expertise: reportHttp.sections.organization.expertise,
      services: reportHttp.sections.organization.services.map((service) => ({
        title: service.title,
        description: service.description,
      })),
    },
    property: {
      isEnabled: reportHttp.sections.property.is_enabled,
      description: reportHttp.sections.property.description,
      surroundingsDescription:
        reportHttp.sections.property.surroundings_description,
      occupation: reportHttp.sections.property.occupation,
      viewingDate: reportHttp.sections.property.viewing_date,
      condition: reportHttp.sections.property.condition,
      comments: reportHttp.sections.property.comments,
    },
    photographs: {
      isEnabled: reportHttp.sections.photographs?.is_enabled,
      photographs: Array.from(
        new Map(
          reportHttp.sections.photographs?.photographs.map((photograph) => [
            photograph.index,
            {
              title: photograph.title,
              index: photograph.index,
            },
          ]),
        ).values(),
      ).sort((a, b) => a.index - b.index),
    },
    case: {
      isEnabled: reportHttp.sections.case.is_enabled,
      address: reportHttp.address_input,
      connectionToTheProperty:
        reportHttp.sections.case.connection_to_the_property,
      date: reportHttp.sections.case.date,
      purpose: reportHttp.sections.case.purpose,
      reference: reportHttp.sections.case.reference,
      comments: reportHttp.sections.case.comments,
    },
    notes: {
      isEnabled: reportHttp.sections.notes.is_enabled,
      content: reportHttp.sections.notes.content,
    },
    definitions: {
      isEnabled: reportHttp.sections.definitions.is_enabled,
      content: reportHttp.sections.definitions.content,
    },
    statistics: reportHttp.sections.statistics
      ? { isEnabled: reportHttp.sections.statistics?.is_enabled }
      : null,
    tendencies: {
      isEnabled: reportHttp.sections.tendencies.is_enabled,
      marketStudy: reportHttp.sections.tendencies.market_study,
      marketStudyScope: reportHttp.sections.tendencies.market_study_scope,
      inseeGraph: reportHttp.sections.tendencies.insee_graph,
    },
    cadastre: {
      isEnabled: reportHttp.sections.cadastre.is_enabled,
      plotPrefixe: reportHttp.sections.cadastre.plot_prefixe,
      plotSection: reportHttp.sections.cadastre.plot_section,
      plotNumber: reportHttp.sections.cadastre.plot_number,
      plotReference: reportHttp.sections.cadastre.plot_reference,
      comment: reportHttp.sections.cadastre.comment,
      plotTotalArea: reportHttp.sections.cadastre.plot_total_area,
      plotBuildingArea: reportHttp.sections.cadastre.plot_building_area,
      plotFreeArea:
        reportHttp.sections.cadastre.plot_total_area &&
        reportHttp.sections.cadastre.plot_building_area
          ? reportHttp.sections.cadastre.plot_total_area -
            reportHttp.sections.cadastre.plot_building_area
          : null,
      buildings: reportHttp.sections.cadastre.buildings.map((building) => ({
        materials: building.materials,
        area: building.area,
      })),
    },
  },
  geo: {
    addressType: reportHttp.geo.address_type,
    districtId: reportHttp.geo.district_id,
    districtLabel: reportHttp.geo.district_label,
    cityId: reportHttp.geo.city_id,
    cityLabel: reportHttp.geo.city_label,
    epciId: reportHttp.geo.epci_id,
    epciLabel: reportHttp.geo.epci_label,
    departementId: reportHttp.geo.departement_id,
    departementLabel: reportHttp.geo.departement_label,
  },
});
